import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'

// Dayjs locales
import Fi from 'dayjs/locale/fi'
import En from 'dayjs/locale/en-gb'
import Sv from 'dayjs/locale/sv'

// Extends
import CustomParseFormat from 'dayjs/plugin/customParseFormat'
import Duration from 'dayjs/plugin/duration'
import IsBetween from 'dayjs/plugin/isBetween'
import LocaleData from 'dayjs/plugin/localeData'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import MinMax from 'dayjs/plugin/minMax'
import ObjectSupport from 'dayjs/plugin/objectSupport'
import QuarterOfYear from 'dayjs/plugin/quarterOfYear'
import RelativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import UTC from 'dayjs/plugin/utc'
import { Language } from '../types/user/User'

// Language-specific date formats
export type DateFormat = {
  standard: string
  standardWithTime: string
  long: string
  tableMonth: string
}

// ei ole implementoitu näitä
export const DATE_FORMATS: Record<Language, DateFormat> = {
  [Language.fi]: {
    standard: 'DD.MM.YYYY',
    standardWithTime: 'DD.MM.YYYY HH:mm',
    long: 'D. MMMM[ta] YYYY',
    tableMonth: 'MM/YYYY'
  },
  [Language.en]: {
    standard: 'MM/DD/YYYY',
    standardWithTime: 'MM/DD/YYYY HH:mm',
    long: 'MMMM D, YYYY',
    tableMonth: 'MM/YYYY'
  },
  [Language.sv]: {
    standard: 'YYYY-MM-DD',
    standardWithTime: 'YYYY-MM-DD HH:mm',
    long: 'D MMMM YYYY',
    tableMonth: 'MM/YYYY'
  }
}

// Application timezone - all dates are stored in backend using this timezone
export const APP_TIMEZONE = 'Europe/Helsinki'

// Validate and parse date string to dayjs object
const validateAndParseDateString = (date: string | null | undefined): Dayjs | null => {
  if (!date) return null
  const parsed = dayjs(date)
  if (!parsed.isValid()) {
    return null
  }
  return parsed
}

// Smart date parsing that detects if date is in UTC or Helsinki timezone
export const parseDate = (date: string | null | undefined): Dayjs | null => {
  const parsed = validateAndParseDateString(date)
  if (!parsed) return null

  // If YYYY-MM-DD format (date only), no timezone handling needed
  if (/^\d{4}-\d{2}-\d{2}$/.test(date!)) {
    return dayjs(date)
  }

  // Check if date is at UTC midnight (00:00:00Z or 00:00:00.000Z)
  if (parsed && /Z$/.test(date!)) {
    try {
      const utcParsed = dayjs.utc(date)
      const utcHour = utcParsed.hour()
      const utcMinute = utcParsed.minute()
      const utcSecond = utcParsed.second()
      const utcMillisecond = utcParsed.millisecond()

      if (utcHour === 0 && utcMinute === 0 && utcSecond === 0 && utcMillisecond === 0) {
        // UTC midnight date - use UTC date directly
        return utcParsed
      }
      // If not midnight, convert UTC time to Helsinki timezone
      return parsed.tz(APP_TIMEZONE)
    } catch (error) {
      return parsed
    }
  }
  // Other dates - interpret in Helsinki time
  try {
    return parsed.tz(APP_TIMEZONE)
  } catch (error) {
    return parsed
  }
}

export const parseDateTime = (date: string | null | undefined): Dayjs | null => {
  const parsed = validateAndParseDateString(date)
  if (!parsed) return null

  // Parse the ISO timestamp and convert to Helsinki timezone, then extract just the date part
  // and create a new local dayjs object so DatePicker shows correct date regardless of user's timezone
  // Parse the ISO timestamp and convert to Helsinki timezone
  try {
    return parsed.tz(APP_TIMEZONE)
  } catch (error) {
    return parsed
  }
}

// Format date to backend format (YYYY-MM-DD string) with smart timezone handling
// This ensures the date is sent as the correct day in Helsinki timezone
// Accepts: Dayjs objects, ISO strings, YYYY-MM-DD strings, Date objects
export const formatAsDate = (date: Dayjs | string | Date | null | undefined): string | undefined => {
  if (!date) return undefined

  // If it's already a Dayjs object, format directly
  if (dayjs.isDayjs(date)) {
    return date.format('YYYY-MM-DD')
  }

  // For strings and Date objects, use parseDate for proper timezone handling
  const parsed = parseDate(typeof date === 'string' ? date : date.toISOString())
  return parsed ? parsed.format('YYYY-MM-DD') : undefined
}

// Convert any date value (ISO string, Date object, etc.) to YYYY-MM-DD format for forms
// This is a convenience function that handles different input types and ensures correct timezone conversion
export const toFormDate = (date: string | Date | Dayjs | null | undefined): string | undefined => {
  if (!date) return undefined

  // If it's already a Dayjs object, format it directly
  if (dayjs.isDayjs(date)) {
    return formatAsDate(date)
  }

  // If it's a Date object, convert to dayjs first
  if (date instanceof Date) {
    return formatAsDate(date)
  }

  // If it's a string (ISO format), parse it correctly then format
  return formatAsDate(parseDate(date))
}

// Helper functions for common timezone-safe operations
// NOTE: These return what the time/date IS RIGHT NOW in Helsinki timezone
export const now = (): Dayjs => {
  try {
    // Try to use timezone functions if available
    if (dayjs().tz && typeof dayjs().tz === 'function') {
      return dayjs().tz(APP_TIMEZONE)
    }
    return dayjs()
  } catch (error) {
    return dayjs()
  }
}
export const today = (): Dayjs => now().startOf('day')
export const thisYear = (): Dayjs => now().startOf('year')
export const thisMonth = (): Dayjs => now().startOf('month')

// Safe date creation from year/month/day numbers
export const createDate = (year: number, month: number, day = 1): Dayjs | null => {
  const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  return parseDate(dateString)
}

// Simple helpers for common backend date formatting patterns
export const endOfMonth = (date: string | Dayjs | null | undefined): string => {
  if (typeof date === 'string') {
    return formatAsDate(parseDate(date)?.endOf('month')) || ''
  }
  if (dayjs.isDayjs(date)) {
    return formatAsDate(date.endOf('month')) || ''
  }
  return ''
}

export const startOfMonth = (date: string | Dayjs | null | undefined): string => {
  if (typeof date === 'string') {
    return formatAsDate(parseDate(date)?.startOf('month')) || ''
  }
  if (dayjs.isDayjs(date)) {
    return formatAsDate(date.startOf('month')) || ''
  }
  return ''
}

export const endOfYear = (date: string | Dayjs | null | undefined): string => {
  if (typeof date === 'string') {
    return formatAsDate(parseDate(date)?.endOf('year')) || ''
  }
  if (dayjs.isDayjs(date)) {
    return formatAsDate(date.endOf('year')) || ''
  }
  return ''
}

export const startOfYear = (date: string | Dayjs | null | undefined): string => {
  if (typeof date === 'string') {
    return formatAsDate(parseDate(date)?.startOf('year')) || ''
  }
  if (dayjs.isDayjs(date)) {
    return formatAsDate(date.startOf('year')) || ''
  }
  return ''
}

export default () => {
  dayjs.locale(Fi)
  dayjs.locale(En)
  dayjs.locale(Sv)

  dayjs.extend(CustomParseFormat)
  dayjs.extend(Duration)
  dayjs.extend(IsBetween)
  dayjs.extend(LocaleData)
  dayjs.extend(LocalizedFormat)
  dayjs.extend(MinMax)
  dayjs.extend(ObjectSupport)
  dayjs.extend(QuarterOfYear)
  dayjs.extend(RelativeTime)
  dayjs.extend(UTC)
  dayjs.extend(timezone)
  dayjs.extend(updateLocale)

  dayjs.tz.setDefault('Europe/Helsinki')
  dayjs.updateLocale('sv', {
    weekStart: 1
  })
  dayjs.updateLocale('en', {
    weekStart: 1
  })
}
